import { type VirtualItem, type Virtualizer, createVirtualizer } from "@tanstack/solid-virtual";
import { type FlowComponent, For, type JSX, onCleanup, onMount } from "solid-js";
import { InfiniteFeedWrapper } from "./styled";

const InfiniteFeed: FlowComponent<
	{
		count: number;
		estimateSize: (index: number) => number;
		onInterSection: () => void;
		overscan?: number;
		loading?: boolean;
		loadingCount?: number;
		onVirtualizer?: (virtualizer: Virtualizer<HTMLDivElement, Element>) => void;
		isReverse?: boolean;
	},
	(item: VirtualItem) => JSX.Element
> = (props) => {
	let scrollElement: HTMLDivElement | undefined;

	const invertedWheelScroll = (event: WheelEvent) => {
		if (!scrollElement || !props.isReverse) return;
		scrollElement.scrollTop -= event.deltaY;
		event.preventDefault();
	};

	const handleScroll = () => {
		if (!scrollElement) return;
		const { scrollHeight, scrollTop, clientHeight } = scrollElement;
		if (scrollHeight - scrollTop - clientHeight < 300) props.onInterSection();
	};

	onMount(() => {
		if (!scrollElement) return;
		scrollElement.onscroll = handleScroll;
		scrollElement.onwheel = invertedWheelScroll;
	});

	onCleanup(() => {
		scrollElement?.removeEventListener("scroll", handleScroll);
		scrollElement?.removeEventListener("wheel", invertedWheelScroll);
	});

	const rowVirtualizer = createVirtualizer({
		get count() {
			return props.count + (props.loading ? props.loadingCount ?? 2 : 0);
		},
		getScrollElement: () => scrollElement ?? null,
		get estimateSize() {
			return props.estimateSize;
		},
		get overscan() {
			return props.overscan;
		},
	});

	props.onVirtualizer?.(rowVirtualizer);

	return (
		<InfiniteFeedWrapper ref={scrollElement} isReverse={props.isReverse}>
			<div
				style={{
					height: `${rowVirtualizer.getTotalSize()}px`,
				}}
			>
				<div
					style={{
						transform: `translateY(${rowVirtualizer.getVirtualItems()[0]?.start ?? 0}px)`,
					}}
				>
					<For each={rowVirtualizer.getVirtualItems()}>
						{(item) => {
							return (
								<div
									class="virtualizerItem"
									data-index={item.index}
									ref={(el) => onMount(() => rowVirtualizer.measureElement(el))}
								>
									{props.children(item)}
								</div>
							);
						}}
					</For>
				</div>
			</div>
		</InfiniteFeedWrapper>
	);
};
export default InfiniteFeed;
